/**
 * Units of Measure Page - with Server-Side Pagination, Sorting and Selection
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../lib/api';

import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { DataGrid } from '../../components/ui/DataGrid';
import { PageHeader } from '../../components/ui/PageHeader';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Plus, Ruler, Edit, Trash2, Download, FileText, FileSpreadsheet, ChevronDown, CheckSquare, CheckCircle, XCircle, Settings, Database } from 'lucide-react';

import UnitOfMeasureModal from './UnitOfMeasureModal';

export default function UnitsOfMeasure() {
  const { t } = useTranslation();
  const gridRef = useRef(null);

  // Data state
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  // Server-side pagination state
  const [totalItems, setTotalItems] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filters, setFilters] = useState({});

  // Selection state
  const [selectedCount, setSelectedCount] = useState(0);

  // Quick filter state
  const [quickFilter, setQuickFilter] = useState(null);

  // Load units with server-side params
  const loadUnits = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build filters object
      const apiFilters = {};
      if (filters.name) apiFilters.name = filters.name;
      if (filters.status && filters.status !== 'all') apiFilters.is_active = filters.status === 'active';

      // Apply quick filter
      if (quickFilter === 'active') apiFilters.is_active = true;
      if (quickFilter === 'inactive') apiFilters.is_active = false;
      if (quickFilter === 'system') apiFilters.is_system = true;
      if (quickFilter === 'custom') apiFilters.is_system = false;

      const response = await api.post('/units-of-measure/list', {
        pageNumber,
        pageSize,
        sortBy,
        sortDirection,
        filters: Object.keys(apiFilters).length > 0 ? apiFilters : undefined,
      });

      if (response.data?.data?.items) {
        setUnits(response.data.data.items);
        setTotalItems(response.data.data.totalItems || 0);
      } else {
        setUnits([]);
        setTotalItems(0);
      }
    } catch (err) {
      console.error('Error loading units:', err);
      toast.error('Erro ao carregar unidades de medida');
    } finally {
      setLoading(false);
    }
  }, [pageNumber, pageSize, sortBy, sortDirection, filters, quickFilter]);

  useEffect(() => {
    loadUnits();
  }, [loadUnits]);

  // Handle page change from grid
  const handlePageChange = useCallback((newPage, newPageSize) => {
    setPageNumber(newPage);
    setPageSize(newPageSize);
  }, []);

  // Handle sort change from grid
  const handleSortChange = useCallback((newSortBy, newSortDirection) => {
    if (newSortBy && newSortDirection) {
      setSortBy(newSortBy);
      setSortDirection(newSortDirection);
    }
  }, []);

  // Handle filter search
  const handleSearch = useCallback(() => {
    setPageNumber(1);
    loadUnits();
  }, [loadUnits]);

  // Quick filter handler
  const handleQuickFilter = useCallback((filter) => {
    setQuickFilter(prev => prev === filter ? null : filter);
    setPageNumber(1);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({});
    setQuickFilter(null);
    setPageNumber(1);
  }, []);

  const handleSave = async (data) => {
    try {
      setSaving(true);
      if (editingUnit?.id) {
        await api.put(`/units-of-measure/${editingUnit.id}`, data);
        toast.success('Unidade de medida atualizada com sucesso');
      } else {
        await api.post('/units-of-measure', data);
        toast.success('Unidade de medida criada com sucesso');
      }
      setModalOpen(false);
      setEditingUnit(null);
      await loadUnits();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar unidade de medida');
    } finally {
      setSaving(false);
    }
  };

  // Handle modal close - reset editingUnit to null
  const handleModalOpenChange = (open) => {
    setModalOpen(open);
    if (!open) {
      setTimeout(() => setEditingUnit(null), 100);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    // Check if unit is a system unit
    const unit = units.find(u => u.id === deleteId);
    if (unit?.is_system) {
      toast.error('Não é possível excluir unidades de medida do sistema');
      setDeleteId(null);
      return;
    }
    
    try {
      await api.delete(`/units-of-measure/${deleteId}`);
      toast.success('Unidade de medida excluída com sucesso');
      setDeleteId(null);
      await loadUnits();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir unidade de medida');
    }
  };

  // Seed default units
  const handleSeedDefaults = async () => {
    setSeeding(true);
    try {
      const response = await api.post('/units-of-measure/seed-defaults');
      toast.success(response.data?.message || 'Unidades padrão criadas com sucesso');
      await loadUnits();
    } catch (err) {
      toast.error('Erro ao criar unidades padrão');
    }
    setSeeding(false);
  };

  // Export handlers
  const handleExportCSV = useCallback((onlySelected = false) => {
    if (onlySelected && selectedCount === 0) {
      toast.error('Selecione pelo menos um item para exportar');
      return;
    }
    gridRef.current?.exportCSV(onlySelected);
  }, [selectedCount]);

  const handleExportJSON = useCallback((onlySelected = false) => {
    if (onlySelected && selectedCount === 0) {
      toast.error('Selecione pelo menos um item para exportar');
      return;
    }
    const data = gridRef.current?.exportJSON(onlySelected);
    if (!data?.length) {
      toast.error('Nenhum dado para exportar');
      return;
    }
    const jsonStr = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `unidades_medida_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [selectedCount]);

  // Track selection changes
  const handleSelectionChanged = useCallback(() => {
    const count = gridRef.current?.getSelectedCount() || 0;
    setSelectedCount(count);
  }, []);

  const filterDefinitions = useMemo(() => [
    {
      key: 'name',
      label: 'Nome',
      type: 'text',
      placeholder: 'Buscar unidade...',
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      placeholder: 'Todos',
      options: [
        { value: 'all', label: 'Todos' },
        { value: 'active', label: 'Ativo' },
        { value: 'inactive', label: 'Inativo' },
      ],
    },
  ], []);

  const columnDefs = useMemo(() => [
    {
      field: 'name',
      headerName: 'Nome',
      flex: 2,
      sortable: true,
      cellRenderer: (params) => (
        <span className="font-medium">{params.value}</span>
      ),
    },
    {
      field: 'abbreviation',
      headerName: 'Abreviação',
      flex: 1,
      sortable: true,
      cellRenderer: (params) => (
        <Badge variant="outline" className="font-mono">
          {params.value}
        </Badge>
      ),
    },
    {
      field: 'description',
      headerName: 'Descrição',
      flex: 2,
      sortable: true,
    },
    {
      field: 'is_system',
      headerName: 'Tipo',
      flex: 1,
      sortable: true,
      cellRenderer: (params) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          params.value 
            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' 
            : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
        }`}>
          {params.value ? 'Sistema' : 'Customizado'}
        </span>
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      flex: 0.8,
      sortable: true,
      cellRenderer: (params) => (
        <span className={params.value ? 'datagrid-status-active' : 'datagrid-status-inactive'}>
          {params.value ? 'Ativo' : 'Inativo'}
        </span>
      ),
    },
    {
      field: 'actions',
      headerName: 'Ações',
      flex: 0.8,
      sortable: false,
      filter: false,
      cellRenderer: (params) => (
        <div className="datagrid-actions">
          <Button variant="ghost" size="icon" onClick={() => { setEditingUnit(params.data); setModalOpen(true); }}>
            <Edit className="h-4 w-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => setDeleteId(params.data.id)}
            disabled={params.data.is_system}
          >
            <Trash2 className={`h-4 w-4 ${params.data.is_system ? 'text-muted' : 'text-destructive'}`} />
          </Button>
        </div>
      ),
    },
  ], []);

  // Custom export actions with selection option
  const ExportActions = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm">
          <Download className="h-4 w-4 mr-2" />
          Exportar
          {selectedCount > 0 && (
            <Badge variant="secondary" className="ml-2">{selectedCount}</Badge>
          )}
          <ChevronDown className="h-3 w-3 ml-2" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => handleExportCSV(false)}>
          <FileText className="h-4 w-4 mr-2" />
          Exportar Tudo (CSV)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExportJSON(false)}>
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          Exportar Tudo (JSON)
        </DropdownMenuItem>
        {selectedCount > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handleExportCSV(true)}>
              <CheckSquare className="h-4 w-4 mr-2" />
              Exportar {selectedCount} Selecionados (CSV)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExportJSON(true)}>
              <CheckSquare className="h-4 w-4 mr-2" />
              Exportar {selectedCount} Selecionados (JSON)
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="units-of-measure-page" data-testid="units-of-measure-page">
      <PageHeader
        title="Unidades de Medida"
        icon={Ruler}
        subtitle={`${totalItems} unidades cadastradas`}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleSeedDefaults} disabled={seeding} data-testid="seed-defaults-btn">
              <Database className="h-4 w-4 mr-2" />
              {seeding ? 'Carregando...' : 'Carregar Padrões'}
            </Button>
            <ExportActions />
            <Button onClick={() => { setEditingUnit(null); setModalOpen(true); }} data-testid="new-unit-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nova Unidade
            </Button>
          </div>
        }
        filters={filterDefinitions}
        filterValues={filters}
        onFilterChange={setFilters}
        onSearch={handleSearch}
        onClearFilters={handleClearFilters}
        showExport={false}
      />

      {/* Quick Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <Button
          variant={quickFilter === 'active' ? 'default' : 'outline'}
          size="sm"
          onClick={() => handleQuickFilter('active')}
          className={quickFilter === 'active' ? '' : 'hover:bg-green-500/10'}
          data-testid="quick-filter-active"
        >
          <CheckCircle className="h-4 w-4 mr-1" />
          Ativos
        </Button>
        <Button
          variant={quickFilter === 'inactive' ? 'default' : 'outline'}
          size="sm"
          onClick={() => handleQuickFilter('inactive')}
          className={quickFilter === 'inactive' ? '' : 'hover:bg-gray-500/10'}
          data-testid="quick-filter-inactive"
        >
          <XCircle className="h-4 w-4 mr-1" />
          Inativos
        </Button>
        <Button
          variant={quickFilter === 'system' ? 'default' : 'outline'}
          size="sm"
          onClick={() => handleQuickFilter('system')}
          className={quickFilter === 'system' ? '' : 'hover:bg-blue-500/10'}
          data-testid="quick-filter-system"
        >
          <Settings className="h-4 w-4 mr-1" />
          Sistema
        </Button>
        <Button
          variant={quickFilter === 'custom' ? 'default' : 'outline'}
          size="sm"
          onClick={() => handleQuickFilter('custom')}
          className={quickFilter === 'custom' ? '' : 'hover:bg-purple-500/10'}
          data-testid="quick-filter-custom"
        >
          <Ruler className="h-4 w-4 mr-1" />
          Customizados
        </Button>
        {quickFilter && (
          <Button variant="ghost" size="sm" onClick={() => setQuickFilter(null)}>
            Limpar filtro
          </Button>
        )}
      </div>

      <DataGrid
        ref={gridRef}
        title="Lista de Unidades de Medida"
        columnDefs={columnDefs}
        rowData={units}
        loading={loading}
        enableSelection={true}
        getRowId={(params) => params.data.id}
        serverSide={true}
        totalItems={totalItems}
        pageNumber={pageNumber}
        onPageChange={handlePageChange}
        onSortChange={handleSortChange}
        sortBy={sortBy}
        sortDirection={sortDirection}
        paginationPageSize={pageSize}
        onSelectionChanged={handleSelectionChanged}
      />

      <UnitOfMeasureModal
        open={modalOpen}
        onOpenChange={handleModalOpenChange}
        unit={editingUnit}
        onSave={handleSave}
        loading={saving}
      />

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta unidade de medida? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
